<template>
  <section class="container" aria-label="find your flock">
    <div class="row">
      <!-- Video Section -->
      <div v-if="flockVideo" class="col-lg-5 col-md-12 flock-video">
        <div class="flock-video__main">
          <div class="flock-video__container">
            <ul v-if="this.videos.length" class="flock-video__slides" ref="slide">
              <li class="flock-video__slide" id="slide-1">
                <video width="221" height="391" class="adjust-initial-slide" :data-slide="0" @click="handleVideoSlides"
                  loop autobuffer preload="auto" playsinline :poster="videos[0].poster" :title="videos[0].title">
                  <source :src="videos[0].video" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </li>
              <li class="flock-video__slide" id="slide-2">
                <video width="230" height="409" :data-slide="1" @click="handleVideoSlides" loop autobuffer preload="auto"
                  playsinline :poster="videos[1].poster" :title="videos[1].title">
                  <source :src="videos[1].video" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </li>
              <li class="flock-video__slide" id="slide-3">
                <video width="248" height="436" class="adjust-initial-slide" :data-slide="2" @click="handleVideoSlides"
                  loop autobuffer preload="auto" playsinline :poster="videos[2].poster" :title="videos[2].title">
                  <source :src="videos[2].video" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </li>
              <li class="flock-video__slide" id="slide-4">
                <video width="250" height="445" :data-slide="3" @click="handleVideoSlides" loop autoplay muted autobuffer
                  preload="auto" playsinline :poster="videos[3].poster" :title="videos[3].title">
                  <source :src="videos[3].video" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </li>
            </ul>
          </div>
          <nav class="flock-video__bullets" aria-label="flock-carousel">
            <button v-for="(video, index) in this.videos" :key="index" class="flock-video__bullet" :data-slide="index"
              :id="`button-${index}`" ref="icons" :aria-label="`Video of ${video.name}`"
              :aria-controls="`button-${index}`" @click="handleVideoSlides"></button>
          </nav>
        </div>
      </div>
      <div class="col-lg-1 col-md-12"></div>
      <!-- Quiz Section -->
      <div class="col-lg-6 col-md-12 flock-quiz">
        <div v-if="flock">
          <div v-if="introView">
            <header>
              <h2 id="flock" tabindex="0">{{ flock.flock_title }}</h2>
            </header>
            <p v-html="flock.flock_description" class="flock-description"></p>
            <button class="flock-start-btn" @click="handleQuiz('start')" aria-label="Start Quiz">Start Quiz</button>
          </div>
          <div v-if="quizView">
            <header v-if="questionIndex < quiz.questions.length" :key="'A' + questionIndex">
              <h2 id="flock">{{ flock.flock_title }}</h2>
            </header>
            <!-- Progress Bar -->
            <div v-if="questionIndex < quiz.questions.length" class="flock-progress">
              <div class="progress-container">
                <b-progress :value="progressValue" :max="progressMax" show-progress class="mb-3">
                  <b-progress-bar :value="progressValue === 10 ? progressValue + 1 : progressValue"
                    :label="`${progressValue}%`" class="progress-bar"></b-progress-bar>
                </b-progress>
              </div>
            </div>
            <!-- Questions -->
            <div class="flock-qa">
              <div v-if="questionIndex < quiz.questions.length" :key="'C' + questionIndex" class="flock-qa-group">
                <p class="flock-questions">
                  {{ quiz.questions[questionIndex].id }}. {{ quiz.questions[questionIndex].text }}
                </p>
                <div class="flock-answers" v-for="(response, index) in quiz.questions[questionIndex].responses"
                  @click="selectOption(index)" :key="'D' + index">
                  <label> <input type="radio" name="checkbox" :id="`response_${index}`" /> {{ response.text }} </label>
                </div>
              </div>
              <footer v-if="questionIndex < quiz.questions.length" :key="'E' + questionIndex" class="flock-footer">
                <button class="col-lg-4 col-sm-12 flock-next-btn"
                  :class="userResponses[questionIndex] == null ? '' : 'is-active'"
                  :disabled="userResponses[questionIndex] == null" @click="
                    advanceQuestion();
                  handleProgressBar();
                  calculateWinner();
                  ">
                  Next
                </button>
                <button class="flock-back-to-start" @click="handleQuiz('restart')">Back to Start</button>
              </footer>
            </div>
          </div>
        </div>
      </div>
      <!-- Results Section-->
      <div v-if="questionIndex >= quiz.questions.length" :key="'F' + questionIndex" class="col-md-12">
        <header>
          <h2 id="flock">{{ flock.flock_title }}</h2>
        </header>
        <button class="flock-progress flock-back-to-start-results" @click="handleQuiz('restart')"
          id="flock-back-to-start-results">
          Back to Start
        </button>
        <div>
          <div class="results-card">
            <div class="results-card-body row">
              <div class="results-img-container d-flex justify-content-center col-lg-2 col-md-12 mr-3">
                <b-img class="results-img" :src="winnersImg" rounded="circle" :alt="winnersImageAlt" />
              </div>
              <div class="col-lg-9 col-md-12 results-media-body">
                <h3 class="results-title">{{ winnersTitle }}</h3>
                <p class="results-content">{{ winnersDescription }}</p>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h2 class="flock-trails-title">Trails for {{ winnersTitle }} Personalities</h2>
          <!-- Personality Cards -->
          <div class="d-flex justify-content-center">
            <b-card-group class="trails-deck">
              <div class="col-lg-4 col-md-8 trails-card">
                <b-row no-gutters>
                  <b-col md="12">
                    <div>
                      <b-card-text class="trails-body">
                        <h3 class="trails-title">{{ winnersTrailTitle1 }}</h3>
                        <CountyMap :region="regionClass1" class="state-map"
                          :counties="countyList1.map(c => c.toLowerCase())" :ScaleX="1" :ScaleY="1" :Rotate="-7" />
                        <p class="trails-content" :class="winnersDescMinHeight">{{ winnersTrailDescription1 }}</p>
                      </b-card-text>
                    </div>
                  </b-col>
                </b-row>
                <a :href="winnersTrailLink1" target="_blank" rel="noopener noreferrer" class="flock-view-the-trail-btn" aria-label="View the Trail">
                  View the Trail
                  <img src="/images/icons/external-link-icon.svg" alt="external link" class="btn-external-link" />
                </a>
              </div>
              <div class="col-lg-4 col-md-8 trails-card">
                <b-row no-gutters>
                  <b-col md="12">
                    <div>
                      <b-card-text class="trails-body">
                        <h3 class="trails-title">{{ winnersTrailTitle2 }}</h3>
                        <CountyMap :region="regionClass2" class="state-map"
                          :counties="countyList2.map(c => c.toLowerCase())" :ScaleX="1" :ScaleY="1" :Rotate="-7" />
                        <p class="trails-content" :class="winnersDescMinHeight">{{ winnersTrailDescription2 }}</p>
                      </b-card-text>
                    </div>
                  </b-col>
                </b-row>
                <a :href="winnersTrailLink2" target="_blank" rel="noopener noreferrer" class="flock-view-the-trail-btn" aria-label="View the Trail">
                  View the Trail
                  <img src="/images/icons/external-link-icon.svg" alt="external link" class="btn-external-link" />
                </a>
              </div>
            </b-card-group>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import CountyMap from '@/components/PlacesToGo/Common/CountyMap.vue';
import Vue from 'vue';
import quiz from '../../../../public/json/findyourflock.json';
import { shuffleArray } from '../../../store/helpers/helpers';

const userResponses = Array(quiz.questions.length).fill(null);

export default {
  name: 'findyourflock',
  props: {
    flock: {
      type: Object,
    },
  },
  components: {
    CountyMap,
  },
  created() {
    this.setVideos();
    this.shuffleVideos(this.videos);
  },
  data() {
    return {
      videos: [],
      flockVideo: true,
      introView: true,
      quizView: false,
      currentActive: 1,
      quiz,
      userResponses,
      questionIndex: 0,
      winningPersonality: '',
      progressValue: 0,
      progressMax: 100,
    };
  },
  methods: {
    setVideos() {
      this.videos = [
        {
          video: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/Eagle_v01.mp4',
          poster: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/eagle_poster.png',
          title: this.flock.eagle_video_alt_text,
          name: 'Eagle',
        },
        {
          video: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/NorthernFlicker_v01.mp4',
          poster: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/northernflicker_poster.png',
          title: this.flock.flicker_video_alt_text,
          name: 'Northern Flicker',
        },
        {
          video: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/Owl_v01+(1).mp4',
          poster: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/owl_poster.png',
          title: this.flock.owl_video_alt_text,
          name: 'Owl',
        },
        {
          video: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/GBH_v01+(1).mp4',
          poster: 'https://alabama-travel.s3.amazonaws.com/videos/flock_videos/gbh_poster.png',
          title: this.flock.heron_video_alt_text,
          name: 'Great Blue Heron',
        },
      ];
    },
    handleVideoSlides(event) {
      const activeSlide = Number(event.target.dataset.slide);
      this.resetActiveClass();
      this.handleActiveSlide(activeSlide);
    },
    resetActiveClass() {
      for (let index = 0; index < this.videos.length; index++) {
        this.$refs.slide.children[index].classList.remove('active');
        this.$refs.icons[index].classList.remove('active');
      }
    },
    handleActiveSlide(activeSlide) {
      switch (activeSlide) {
        case Number(0):
          this.$refs.slide.children[0].classList.add('active');
          this.$refs.icons[0].classList.add('active');
          this.$refs.slide.children[0].children[0].style.width = '250px';
          this.$refs.slide.children[0].children[0].style.height = '445px';
          this.$refs.slide.children[0].style.zIndex = '1000';
          this.$refs.slide.children[0].style.transform = 'scale(1)';
          this.$refs.slide.children[1].children[0].style.width = '230px';
          this.$refs.slide.children[1].children[0].style.height = '409px';
          this.$refs.slide.children[1].children[0].style.position = 'absolute';
          this.$refs.slide.children[1].children[0].style.left = '2px';
          this.$refs.slide.children[1].style.zIndex = '2';
          this.$refs.slide.children[1].style.transform = 'scale(1, 0.95)';
          this.$refs.slide.children[2].children[0].style.width = '248px';
          this.$refs.slide.children[2].children[0].style.height = '436px';
          this.$refs.slide.children[2].style.zIndex = '1';
          this.$refs.slide.children[2].style.transform = 'scale(1, 0.90)';
          this.$refs.slide.children[3].children[0].style.width = '250px';
          this.$refs.slide.children[3].children[0].style.height = '445px';
          this.$refs.slide.children[3].style.zIndex = '0';
          this.$refs.slide.children[3].style.transform = 'scale(1, 0.85)';
          this.handleAutoplay(activeSlide);
          break;
        case Number(1):
          this.$refs.slide.children[1].classList.add('active');
          this.$refs.icons[1].classList.add('active');
          this.$refs.slide.children[1].children[0].style.width = '250px';
          this.$refs.slide.children[1].children[0].style.height = '445px';
          this.$refs.slide.children[1].children[0].style.position = 'absolute';
          this.$refs.slide.children[1].children[0].style.left = '0px';
          this.$refs.slide.children[1].style.zIndex = '1000';
          this.$refs.slide.children[1].style.transform = 'scale(1)';
          this.$refs.slide.children[2].children[0].style.width = '248px';
          this.$refs.slide.children[2].children[0].style.height = '436px';
          this.$refs.slide.children[2].style.zIndex = '2';
          this.$refs.slide.children[2].style.transform = 'scale(1, 0.95)';
          this.$refs.slide.children[3].children[0].style.width = '250px';
          this.$refs.slide.children[3].children[0].style.height = '445px';
          this.$refs.slide.children[3].style.zIndex = '1';
          this.$refs.slide.children[3].style.transform = 'scale(1, 0.90)';
          this.$refs.slide.children[0].children[0].style.width = '221px';
          this.$refs.slide.children[0].children[0].style.height = '391px';
          this.$refs.slide.children[0].style.zIndex = '0';
          this.$refs.slide.children[0].style.transform = 'scale(1, 0.85)';
          this.handleAutoplay(activeSlide);
          break;
        case Number(2):
          this.$refs.slide.children[2].classList.add('active');
          this.$refs.icons[2].classList.add('active');
          this.$refs.slide.children[2].children[0].style.width = '250px';
          this.$refs.slide.children[2].children[0].style.height = '445px';
          this.$refs.slide.children[2].style.zIndex = '1000';
          this.$refs.slide.children[2].style.transform = 'scale(1)';
          this.$refs.slide.children[3].children[0].style.width = '248px';
          this.$refs.slide.children[3].children[0].style.height = '436px';
          this.$refs.slide.children[3].style.zIndex = '2';
          this.$refs.slide.children[3].style.transform = 'scale(1, 0.95)';
          this.$refs.slide.children[1].children[0].style.width = '230px';
          this.$refs.slide.children[1].children[0].style.height = '409px';
          this.$refs.slide.children[1].children[0].style.position = 'absolute';
          this.$refs.slide.children[1].children[0].style.left = '0px';
          this.$refs.slide.children[1].style.zIndex = '1';
          this.$refs.slide.children[1].style.transform = 'scale(1, 0.90)';
          this.$refs.slide.children[0].children[0].style.width = '221px';
          this.$refs.slide.children[0].children[0].style.height = '391px';
          this.$refs.slide.children[0].style.zIndex = '0';
          this.$refs.slide.children[0].style.transform = 'scale(1, 0.85)';
          this.handleAutoplay(activeSlide);
          break;
        case Number(3):
          this.$refs.slide.children[3].classList.add('active');
          this.$refs.icons[3].classList.add('active');
          this.$refs.slide.children[3].children[0].style.width = '250px';
          this.$refs.slide.children[3].children[0].style.height = '445px';
          this.$refs.slide.children[3].style.zIndex = '1000';
          this.$refs.slide.children[3].style.transform = 'scale(1)';
          this.$refs.slide.children[2].children[0].style.width = '248px';
          this.$refs.slide.children[2].children[0].style.height = '436px';
          this.$refs.slide.children[2].children[0].style.position = 'absolute';
          this.$refs.slide.children[2].children[0].style.left = '-2px';
          this.$refs.slide.children[2].style.zIndex = '2';
          this.$refs.slide.children[2].style.transform = 'scale(1, 0.95)';
          this.$refs.slide.children[1].children[0].style.width = '230px';
          this.$refs.slide.children[1].children[0].style.height = '409px';
          this.$refs.slide.children[1].children[0].style.position = 'absolute';
          this.$refs.slide.children[1].children[0].style.left = '0px';
          this.$refs.slide.children[1].style.zIndex = '1';
          this.$refs.slide.children[1].style.transform = 'scale(1, 0.90)';
          this.$refs.slide.children[0].children[0].style.width = '221px';
          this.$refs.slide.children[0].children[0].style.height = '391px';
          this.$refs.slide.children[0].children[0].style.position = 'absolute';
          this.$refs.slide.children[0].children[0].style.left = '-1px';
          this.$refs.slide.children[0].style.zIndex = '0';
          this.$refs.slide.children[0].style.transform = 'scale(1, 0.85)';
          this.handleAutoplay(activeSlide);
          break;
        default:
          break;
      }
    },
    handleAutoplay(active) {
      switch (active) {
        case Number(0):
          this.$refs.slide.children[0].children[0].play();
          this.$refs.slide.children[1].children[0].pause();
          this.$refs.slide.children[2].children[0].pause();
          this.$refs.slide.children[3].children[0].pause();
          break;
        case Number(1):
          this.$refs.slide.children[1].children[0].play();
          this.$refs.slide.children[0].children[0].pause();
          this.$refs.slide.children[2].children[0].pause();
          this.$refs.slide.children[3].children[0].pause();
          break;
        case Number(2):
          this.$refs.slide.children[2].children[0].play();
          this.$refs.slide.children[0].children[0].pause();
          this.$refs.slide.children[1].children[0].pause();
          this.$refs.slide.children[3].children[0].pause();
          break;
        case Number(3):
          this.$refs.slide.children[3].children[0].play();
          this.$refs.slide.children[0].children[0].pause();
          this.$refs.slide.children[1].children[0].pause();
          this.$refs.slide.children[2].children[0].pause();
          break;
        default:
          break;
      }
    },
    handleQuiz(selection) {
      if (selection === 'start') {
        this.showIntro();
      }
      if (selection === 'restart') {
        this.restartQuiz();
      }
    },
    shuffleVideos(arr) {
      const shuffledVideos = arr.sort(() => Math.random() - 0.5);
      this.videos = shuffledVideos;
    },
    showIntro() {
      this.introView = !this.introView;
      this.quizView = !this.quizView;
    },
    restartQuiz() {
      this.showIntro();
      this.questionIndex = 0;
      this.userResponses = Array(this.quiz.questions.length).fill(null);
      this.flockVideo = true;
      this.resetProgressIndicators();
    },
    selectOption(index) {
      Vue.set(this.userResponses, this.questionIndex, index);
    },
    advanceQuestion() {
      // Advance question count
      if (this.currentActive < 11) this.currentActive++;
      // Advance to next question
      if (this.questionIndex < this.quiz.questions.length) {
        this.questionIndex++;
        this.setElementFocus();
      }
      // Upon Quiz completion hide videos
      if (this.questionIndex >= this.quiz.questions.length) this.flockVideo = false;
    },
    setElementFocus() {
      if (this.questionIndex < this.quiz.questions.length) {
        setTimeout(() => {
          document.querySelector('#response_0').focus();
        }, 100);
      } else {
        setTimeout(() => {
          document.querySelector('#flock-back-to-start-results').focus();
        }, 100);
      }
    },
    handleProgressBar() {
      if (this.progressValue <= 99) {
        this.progressValue += 10;
      }
    },
    resetProgressIndicators() {
      this.progressValue = 0;
    },
    calculateWinner() {
      const flock = [
        {
          name: 'eagle',
          total: 0,
        },
        {
          name: 'heron',
          total: 0,
        },
        {
          name: 'owl',
          total: 0,
        },
        {
          name: 'flicker',
          total: 0,
        },
      ];

      for (let i = 0; i < this.userResponses.length; i++) {
        if (
          typeof this.quiz.questions[i].responses[this.userResponses[i]] !== 'undefined'
          && this.quiz.questions[i].responses[this.userResponses[i]].type === 'Bald Eagle'
        ) {
          flock[0].total += this.quiz.questions[i].responses[this.userResponses[i]].point;
        } else if (
          typeof this.quiz.questions[i].responses[this.userResponses[i]] !== 'undefined'
          && this.quiz.questions[i].responses[this.userResponses[i]].type === 'Great Blue Heron'
        ) {
          flock[1].total += this.quiz.questions[i].responses[this.userResponses[i]].point;
        } else if (
          typeof this.quiz.questions[i].responses[this.userResponses[i]] !== 'undefined'
          && this.quiz.questions[i].responses[this.userResponses[i]].type === 'Barn Owl'
        ) {
          flock[2].total += this.quiz.questions[i].responses[this.userResponses[i]].point;
        } else if (
          typeof this.quiz.questions[i].responses[this.userResponses[i]] !== 'undefined'
          && this.quiz.questions[i].responses[this.userResponses[i]].type === 'Northern Flicker'
        ) {
          flock[3].total += this.quiz.questions[i].responses[this.userResponses[i]].point;
        }
      }
      flock.sort((a, b) => b.total - a.total);
      if (flock[0].total === flock[1].total) {
        const tied = [flock[0], flock[1]];
        const shuffled = shuffleArray(tied);
        const winner = shuffled[0].name;
        this.winningPersonality = winner;
      } else {
        const winner = flock[0].name;
        this.winningPersonality = winner;
      }
    },
  },
  computed: {
    countyList1() {
      if (this.winningPersonality === 'eagle') return ['Blount', 'Calhoun', 'Cherokee', 'Cleburne', 'Etowah', 'Jefferson', 'Shelby', 'St. Clair', 'Talladega'];
      if (this.winningPersonality === 'heron') return ['Baldwin', 'Mobile'];
      if (this.winningPersonality === 'owl') return ['Barbour', 'Bullock', 'Covington', 'Crenshaw', 'Dale', 'Geneva', 'Henry', 'Houston', 'Pike'];
      if (this.winningPersonality === 'flicker') return ['Autauga', 'Chambers', 'Chilton', 'Clay', 'Coosa', 'Elmore', 'Lee', 'Randolph', 'Tallapoosa'];

      return null;
    },
    countyList2() {
      if (this.winningPersonality === 'eagle') return ['Colbert', 'Cullman', 'DeKalb', 'Jackson', 'Lauderdale', 'Lawrence', 'Limestone', 'Madison', 'Marshall', 'Morgan', 'Winston'];
      if (this.winningPersonality === 'flicker') return ['Bibb', 'Greene', 'Hale', 'Lamar', 'Marion', 'Pickens', 'Tuscaloosa', 'Walker'];
      if (this.winningPersonality === 'owl') return ['Clarke', 'Conecuh', 'Escambia', 'Monroe', 'Washington'];
      if (this.winningPersonality === 'heron') return ['Butler', 'Choctaw', 'Dallas', 'Lowndes', 'Macon', 'Marengo', 'Montgomery', 'Perry', 'Russell', 'Sumter', 'Wilcox'];

      return null;
    },
    regionClass1() {
      if (this.winningPersonality === 'eagle') return 'north-alabama';
      if (this.winningPersonality === 'heron') return 'beaches-gulf-coast';
      if (this.winningPersonality === 'owl') return 'south-alabama';
      if (this.winningPersonality === 'flicker') return 'central-alabama';

      return null;
    },
    regionClass2() {
      if (this.winningPersonality === 'eagle') return 'north-alabama';
      if (this.winningPersonality === 'heron') return 'south-alabama';
      if (this.winningPersonality === 'owl') return 'south-alabama';
      if (this.winningPersonality === 'flicker') return 'central-alabama';

      return null;
    },
    winnersImg() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_img;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_img;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_img;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_img;

      return null;
    },
    winnersTitle() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_title;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_title;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_title;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_title;

      return null;
    },
    winnersDescription() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_description;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_description;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_description;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_description;

      return null;
    },
    winnersImageAlt() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_image_alt_text;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_image_alt_text;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_image_alt_text;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_image_alt_text;

      return null;
    },
    winnersDescMinHeight() {
      if (this.winningPersonality === 'eagle') return 'trail-card-eagle';
      if (this.winningPersonality === 'heron') return 'trail-card-heron';
      if (this.winningPersonality === 'owl') return 'trail-card-owl';
      if (this.winningPersonality === 'flicker') return 'trail-card-flicker';

      return null;
    },
    winnersTrailTitle1() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_trail_title_1;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_trail_title_1;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_trail_title_1;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_trail_title_1;

      return null;
    },
    winnersTrailDescription1() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_trail_description_1;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_trail_description_1;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_trail_description_1;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_trail_description_1;

      return null;
    },
    winnersTrailLink1() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_trail_link_1;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_trail_link_1;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_trail_link_1;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_trail_link_1;

      return null;
    },
    winnersTrailTitle2() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_trail_title_2;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_trail_title_2;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_trail_title_2;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_trail_title_2;

      return null;
    },
    winnersTrailDescription2() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_trail_description_2;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_trail_description_2;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_trail_description_2;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_trail_description_2;

      return null;
    },
    winnersTrailLink2() {
      if (this.winningPersonality === 'eagle') return this.$props.flock.eagle_trail_link_2;
      if (this.winningPersonality === 'heron') return this.$props.flock.heron_trail_link_2;
      if (this.winningPersonality === 'owl') return this.$props.flock.owl_trail_link_2;
      if (this.winningPersonality === 'flicker') return this.$props.flock.flicker_trail_link_2;

      return null;
    },
  },
};
</script>

<style scoped>
section {
  margin-top: 125px;
  margin-bottom: 125px;
}

h2 {
  line-height: 60px;
}

/* ----- Video Carousel----- */
.adjust-initial-slide {
  position: absolute;
  left: -2px;
}

.flock-video__slides {
  height: 365px;
}

.flock-video__slides {
  width: 100%;
  list-style: none;
  backface-visibility: hidden;
  transform-style: preserve-3d;
  white-space: nowrap;
  flex-wrap: nowrap;
  will-change: transform;
  z-index: 2;
}

.flock-video__slide {
  width: 231px;
  height: 391px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.8);
  border: none;
  border-radius: 5px;
  position: absolute;
}

.flock-video__slide:not(.active):hover,
.flock-video__bullet:not(.active):hover,
.flock-video__arrow:not(.active):hover {
  cursor: pointer;
}

.flock-video__slide>* {
  flex: 1 0 auto;
}

.flock-video__slide>div {
  background: transparent;
}

.flock-video__slide,
.flock-video__slide *,
.flock-video__bullets * {
  transition: all 200ms ease;
}

.flock-video__slide--active {
  z-index: 1001;
}

.flock-video__slide,
.flock-video__bullets,
.flock-video__bullet {
  display: flex;
}

.flock-video__bullets {
  justify-content: center;
}

.flock-video__slide,
.flock-video__bullet {
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
}

button.flock-video__bullet {
  display: block;
  width: 17px;
  height: 17px;
  padding: 0;
  margin-top: 25px;
  margin-right: 5px;
  margin-left: 5px;
  background-color: #eaeaea;
  border: 1px solid transparent;
  border-radius: 50%;
  z-index: 500;
}

button.flock-video__bullet.active {
  background-color: #173945;
  z-index: 1000;
}

#slide-1 {
  left: 0;
  z-index: 100;
  transform: scale(1, 0.85);
}

#slide-2 {
  left: 15%;
  z-index: 200;
  transform: scale(1, 0.90);
}

#slide-3 {
  left: 30%;
  z-index: 300;
  transform: scale(1, 0.95);
}

#slide-4 {
  left: 45%;
  z-index: 400;
  transform: scale(1);
}

/* ----- Progress Indicators ----- */
.progress-container {
  margin-top: -10px;
  margin-bottom: 30px;
}

.progress-container::before {
  content: "";
  background-color: #c4c1c1;
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  height: 0px;
  z-index: -1;
}

.progress-container>.progress {
  width: 215px;
  height: 20px;
  font-size: .53em;
  margin: 0 auto;
  transition: 0.4s ease;
  z-index: 1;
}

.progress-container>.progress>.progress-bar {
  background-color: #173945;
}

.circles>div {
  display: inline-block;
  margin-left: 5px;
}

.circle {
  background: #eaeaea;
  color: #eaeaea;
  border-radius: 50%;
  height: 10px;
  width: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.4s ease;
}

.circle.active {
  background: #173945;
}

/* ----- Radio to Checkboxes ----- */
.flock-answers>label>[type="radio"] {
  appearance: none;
  background-color: #fff;
  width: 19px;
  height: 19px;
  border: 2px solid #173945;
  border-radius: 2px;
  display: inline-grid;
  place-content: center;
  margin-left: -19px;
  margin-right: 10px;
  min-width: 19px !important;
}

.flock-answers>label>input[type="radio"]::before {
  content: "";
  width: 10px;
  height: 10px;
  transform: scale(0);
  transform-origin: bottom left;
  background-color: #fff;
  clip-path: polygon(13% 50%, 34% 66%, 81% 2%, 100% 18%, 39% 100%, 0 71%);
}

.flock-answers>label>input[type="radio"]:checked::before {
  transform: scale(1);
}

.flock-answers>label>input[type="radio"]:checked {
  background-color: #173945;
  border: 2px solid #173945;
}

/* ----- Quiz Section ----- */
.flock-start-btn,
.flock-next-btn,
.flock-view-the-trail-btn {
  font-family: "IdealSans-Medium";
  font-style: normal;
  font-weight: 325;
  font-size: 19px;
  line-height: 23px;
  margin: 0 auto;
  margin-top: 50px;
  color: #f6f4ee;
  padding: 12px 0px;
  width: 234px;
  height: 47px;
  background: #173945;
  border: 1px solid #333333;
  border-radius: 8px;
}

.flock-next-btn {
  min-width: 192px;
}

.flock-view-the-trail-btn {
  padding: 12px 60px;
}

.flock-questions,
.flock-answers {
  text-align: left;
}

.flock-answers {
  margin-left: 25px;
}

.flock-answers>label:hover,
.flock-answers>label>input:hover {
  cursor: pointer;
}

button.flock-answers {
  display: block;
  background: none;
  border: none;
}

.flock-footer {
  display: grid;
  justify-content: center;
  justify-items: center;
  margin-right: 15px;
}

.flock-view-the-trail-btn {
  width: 260px;
  min-width: 192px;
  color: #111111;
  background: #fff;
  border: 1px solid #111111;
}

.flock-start-btn:hover,
.flock-next-btn:hover,
.flock-view-the-trail-btn:hover {
  cursor: pointer;
  box-shadow: 0 2px 27px 0 rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease-in-out;
  text-decoration: underline;
}

/* ----- Results Section ----- */
.btn-external-link {
  margin-left: 5px;
}

.results-card-body {
  display: flex;
  flex-wrap: wrap;
}

.trails {
  padding: 0;
}

.trails-card {
  border-top: none;
  background: #ffffff;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 20px;
  padding: 1.25rem;
  padding-bottom: 30px;
  margin: 15px 0 15px 0;
}

.trails-title {
  display: inline;
  font-family: "klinicslabbold";
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 115px;
  text-align: left;
}

.trails-img {
  float: left;
  width: 59px;
  height: 68px;
  margin-right: 15px;
}

.trails-body,
.trails-content {
  width: 100%;
  font-family: "IdealSans-Book";
  font-style: normal;
  font-weight: 325;
  font-size: 16px;
  line-height: 28px;
  text-align: left !important;
}

.trails-body {
  padding: 0 !important;
}

.trails-content {
  margin-top: -30px;
}

.trails-deck {
  display: grid;
  justify-content: center;
  justify-items: center;
}

.trails-deck.card-group>.card:not(:last-child) {
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  margin-right: 15px;
}

.trails-deck.card-group>.card:last-child {
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
  margin-top: 25px;
}

h2.flock-trails-title {
  margin-top: 45px;
  line-height: 40px;
}

.results-card-body {
  background: rgba(76, 142, 160, 0.05);
  margin-bottom: 28px;
  text-align: left;
}

.results-img-container {
  margin-top: 60px;
  margin-bottom: 10px;
}

.results-img {
  width: 200px;
  height: 200px;
  object-fit: cover;
}

.results-title {
  color: #173945;
  font-family: "klinicslabbold";
  font-size: 36px;
  font-weight: 400;
  line-height: 28px;
  letter-spacing: 0px;
  margin-top: 64px;
  margin-bottom: 15px;
}

.results-content {
  margin-bottom: 75px;
}

.results-card {
  padding: 0 !important;
}

.flock-back-to-start,
.flock-back-to-start-results {
  color: #000000;
  margin-top: 56px;
  margin-bottom: 44px;
  background: none;
  border: none;
}

button.flock-back-to-start-results {
  margin-top: 0;
  margin-bottom: 44px;
}

.flock-back-to-start:hover,
.flock-back-to-start-results:hover {
  cursor: pointer;
  text-decoration: underline;
}

.flock-qa-group {
  display: grid;
  justify-content: center;
}

.state-map {
  width: 60px;
  float: left;
  margin-left: -10px;
  margin-right: 10px;
}

.trail-card-eagle {
  min-height: 175px;
}

.trail-card-heron {
  min-height: 235px;
}

.trail-card-owl,
.trail-card-flicker {
  min-height: 125px;
}

/* ----- Media Queries ----- */
@media only screen and (min-width: 414px) {
  .flock-qa {
    margin-left: 15px;
  }
}

@media only screen and (max-width: 414px) {
  .flock-video {
    margin-left: -8%;
  }

  .flock-video__bullets {
    margin-left: 50px !important;
  }
}

@media only screen and (max-width: 768px) {
  .flock-video__bullets {
    margin-bottom: 104px;
  }

  .flock-video__slides {
    margin-left: 6%;
  }
}

@media only screen and (min-width: 768px) {
  .flock-video__bullets {
    margin-left: -20px;
    margin-bottom: 104px;
  }

  .flock-video__slides {
    margin-left: 10%;
  }
}

@media only screen and (max-width: 930px) {
  .flock-video__bullets {
    margin-left: -20px;
    margin-bottom: 104px;
  }
}

@media only screen and (min-width: 992px) {

  section.container,
  section.container-sm,
  section.container-md,
  section.container-lg {
    min-width: 992px;
  }

  .trail-card-eagle {
    min-height: 200px;
  }

  .trail-card-heron {
    min-height: 255px;
  }

  .trail-card-owl {
    min-height: 150px;
  }

  .flock-view-the-trail-btn {
    margin: 0 0 0 -20px !important;
  }

  .flock-video__slides {
    margin-left: 0;
  }

  #slide-1 {
    left: 0;
    z-index: 100;
    transform: scale(1, 0.85);
  }

  #slide-2 {
    left: 20%;
    z-index: 200;
    transform: scale(1, 0.90);
  }

  #slide-3 {
    left: 40%;
    z-index: 300;
    transform: scale(1, 0.95);
  }

  #slide-4 {
    left: 65%;
    z-index: 400;
    transform: scale(1);
  }

  h2#flock {
    margin-top: 10px;
    margin-bottom: 30px;
    line-height: 10px;
  }

  .flock-description {
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;
    color: #000000;
    margin-top: 43px;
  }

  .flock-qa {
    display: grid;
    justify-content: flex-start;
    align-content: flex-start;
    margin-bottom: 24px;
  }

  .flock-next-btn {
    margin: 0;
  }

  .is-inactive {
    visibility: hidden;
  }

  .flock-progress {
    display: grid;
    justify-content: center;
  }

  .flock-footer {
    display: flex;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: center;
    margin-top: 24px;
  }

  .flock-back-to-start {
    margin: 10px 0 0 70px;
  }

  .flock-back-to-start-results {
    margin: -15px auto 44px;
  }

  h2.flock-trails-title {
    margin: 0px auto 24px;
    margin-top: 0px !important;
    font-size: 24px;
    font-weight: 400;
    line-height: 40px;
  }

  .results-card {
    border: 0;
    justify-content: center;
    padding: 1.25rem !important;
  }

  .results-card-body {
    background: rgba(76, 142, 160, 0.05);
    border-radius: 200px;
    padding: 56px;
    margin-bottom: 28px;
  }

  .results-media-body {
    text-align: left;
    margin-left: 3rem;
  }

  .results-img-container {
    margin-top: 0px;
  }

  .results-title {
    margin-top: 0;
    margin-bottom: 10px;
  }

  .results-content {
    font-size: 16px;
    margin-bottom: 0;
  }

  .results-body {
    font-family: "IdealSans-Book";
    font-size: 16px;
    font-weight: 325;
    line-height: 28px;
    letter-spacing: 0px;
  }

  .trails-title {
    line-height: 28px;
  }

  .trails-content {
    margin-top: 8px;
  }

  .trails-img {
    float: right;
    width: 59px;
    height: 68px;
    margin-left: 4px;
  }

  .state-map {
    float: right;
    margin-left: 0;
    margin-right: -10px;
  }

  .trails-card {
    margin: 0 25px 0 25px;
  }

  .trails-deck {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: horizontal;
    -webkit-box-direction: normal;
    -ms-flex-flow: row wrap;
    flex-flow: row wrap;
    margin-right: -15px;
    margin-left: -15px;
  }

  .trails-deck.card-group>.card:last-child {
    margin-top: 0px;
  }

  .flock-qa-group {
    display: block;
    text-align: left;
  }
}

@media only screen and (min-width: 1200px) {
  .flock-view-the-trail-btn {
    margin: 0 0 0 -68px !important;
  }
}
</style>
